import { useEffect } from "react";
import { router } from "@inertiajs/react";
import { Layout } from "@/components/layout/Layout";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Coins, CreditCard, QrCode, Sparkles } from "lucide-react";

interface Product {
  id: number;
  paid_credits: number;
  priceFormatted: string;
}

interface Props {
  products: Product[];
  user: {
    email: string;
    paid_credits: number;
  };
}

export default function Index({ products, user }: Props) {
  useEffect(() => {
    posthog.capture("pricing_viewed");
  }, []);

  const handleCheckout = (productId: number, method: "stripe" | "pix") => {
    if (method === "pix") {
      router.visit(`/paid_credits/pix_checkout?product_id=${productId}`);
    } else {
      router.visit(`/paid_credits/checkout?product_id=${productId}`);
    }
  };

  return (
    <Layout title="Comprar Créditos" user={user}>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <header className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20">
            <Coins className="h-8 w-8 text-amber-400" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">Comprar Créditos</h1>
          <p className="mt-2 text-muted-foreground">Compre créditos para impulsionar suas fotos</p>

          <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-border bg-card px-6 py-3">
            <Sparkles className="h-5 w-5 text-amber-400" />
            <span className="text-muted-foreground">Seu saldo:</span>
            <span className="text-2xl font-bold text-foreground">{user.paid_credits}</span>
            <span className="text-muted-foreground">créditos</span>
          </div>
        </header>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              featured={index === 2}
              onCheckout={handleCheckout}
            />
          ))}
        </div>

        <footer className="mt-10 text-center text-sm text-muted-foreground">
          <p>Pagamentos seguros via Stripe e PIX</p>
        </footer>
      </div>
    </Layout>
  );
}

interface ProductCardProps {
  product: Product;
  featured?: boolean;
  onCheckout: (productId: number, method: "stripe" | "pix") => void;
}

function ProductCard({ product, featured, onCheckout }: ProductCardProps) {
  return (
    <Card
      className={cn(
        "relative overflow-hidden border-border bg-card transition-all hover:border-border/80 hover:bg-card/80",
        featured && "ring-2 ring-amber-500/50"
      )}
    >
      {featured && (
        <div className="absolute right-0 top-0 rounded-bl-lg bg-amber-500 px-3 py-1 text-xs font-semibold text-black">
          Popular
        </div>
      )}

      <CardHeader className="pb-2 text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20">
          <Coins className="h-6 w-6 text-amber-400" />
        </div>
        <CardTitle className="text-4xl font-bold">{product.paid_credits}</CardTitle>
        <p className="text-sm text-muted-foreground">créditos</p>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="text-center">
          <span className="text-2xl font-semibold text-foreground">{product.priceFormatted}</span>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={() => onCheckout(product.id, "pix")}
            className="w-full gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-500 hover:to-emerald-500"
          >
            <QrCode className="h-4 w-4" />
            Pagar com PIX
          </Button>
          <Button
            onClick={() => onCheckout(product.id, "stripe")}
            variant="outline"
            className="w-full gap-2"
          >
            <CreditCard className="h-4 w-4" />
            Cartão de Crédito
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
